import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Archive,
  ArrowDown,
  ArrowUp,
  CreditCard,
  Pencil,
  Plus,
  Wallet,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { PaymentMethodSummary } from '@aeris/contracts'
import { formatPercent, fromString } from '@aeris/money'
import { PAYMENT_METHOD_KIND_LABELS } from '@aeris/payments'
import {
  Badge,
  Button,
  EmptyState,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  SortableHeaderCell,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import { MoneddSettings } from '../components/monedd-settings.tsx'
import { PaymentMethodForm } from '../components/payment-method-form.tsx'
import {
  archivePaymentMethod,
  fetchPaymentMethods,
  installDefaultPaymentMethods,
  reorderPaymentMethods,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { sortKey, useTableSort } from '../lib/use-table-sort.ts'

/**
 * The payment-method catalogue.
 *
 * Configuration, not a ledger: this screen decides which methods the business
 * accepts and at what cost. It is gated on `payment.method.manage`, which an
 * operator does **not** hold — taking a payment is counter work; deciding that
 * the shop accepts boleto at 2,5% is not. `payment.read` is enough to see the
 * list, because somebody has to see the buttons to press one.
 *
 * The whole list is loaded at once, deliberately, with no pagination and no
 * search. A shop has five to a dozen payment methods; a search box over eight
 * rows is a control that only ever gets in the way. Section 27's rules are
 * about lists that can grow to a hundred thousand rows, and this is not one.
 *
 * The order matters and is editable, because it is the order the till will
 * show the buttons in — and that order is "how often", which no sort can know.
 */

const feeLabel = (feePercent: string): string => {
  const parsed = fromString(feePercent)
  return parsed.units === 0n ? '—' : formatPercent(parsed)
}

const settlementLabel = (method: PaymentMethodSummary): string =>
  method.settlesImmediately
    ? 'Na hora'
    : `${String(method.settlementDays)} dia${method.settlementDays === 1 ? '' : 's'}`

export const FormasDePagamentoPage = (): ReactNode => {
  const { allows } = useAuth()
  const queryClient = useQueryClient()

  const [status, setStatus] = useState<'active' | 'archived'>('active')
  const [editing, setEditing] = useState<PaymentMethodSummary | undefined>(
    undefined
  )
  const [formOpen, setFormOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Hiding, not protecting: the API refuses the request independently.
  const canManage = allows('payment.method.manage')

  /*
   * Defaults to the hand-made order, which is the meaningful one.
   *
   * Sorting by name here finds a row in a long list; it does not change what
   * the counter offers. The digit shortcut in the PDV follows `displayOrder`
   * wherever this screen happens to be sorted.
   */
  const sort = useTableSort<'displayOrder' | 'name'>('displayOrder')

  const query = useQuery({
    queryKey: ['payment-methods', { status, ...sortKey(sort) }],
    queryFn: ({ signal }) =>
      fetchPaymentMethods(
        { status, sort: sort.sort, direction: sort.direction },
        signal
      ),
  })

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
  }

  const archive = useMutation({
    mutationFn: async (method: PaymentMethodSummary) =>
      await archivePaymentMethod(method.id, method.version),
    onSuccess: refresh,
    onError: () => {
      setError('Não foi possível arquivar. Recarregue e tente novamente.')
    },
  })

  const installDefaults = useMutation({
    mutationFn: installDefaultPaymentMethods,
    onSuccess: refresh,
  })

  const reorder = useMutation({
    mutationFn: async (methodIds: string[]) =>
      await reorderPaymentMethods(methodIds),
    onSuccess: refresh,
  })

  const methods = query.data?.methods ?? []

  /**
   * Moves one row and sends the whole order.
   *
   * The whole list rather than a delta, because the server writes it in one
   * statement — a sequence of single-row updates has a window in which the
   * order on screen is neither the old one nor the new one.
   */
  const move = (index: number, delta: number): void => {
    const next = [...methods]
    const target = index + delta
    const moved = next[index]
    const displaced = next[target]
    if (moved === undefined || displaced === undefined) return

    next[index] = displaced
    next[target] = moved
    reorder.mutate(next.map((method) => method.id))
  }

  const openNew = (): void => {
    setEditing(undefined)
    setFormOpen(true)
  }

  const openEdit = (method: PaymentMethodSummary): void => {
    setEditing(method)
    setFormOpen(true)
  }

  return (
    <>
      <TabHeader
        title="Formas de pagamento"
        description="O que a empresa aceita, com que taxa e em quantos dias o dinheiro entra."
        actions={
          canManage ? (
            <Button
              variant="primary"
              size="sm"
              onClick={openNew}
              leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
            >
              Nova forma
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as 'active' | 'archived')
          }}
          aria-label="Situação"
          className="w-44"
        >
          <option value="active">Ativas</option>
          <option value="archived">Arquivadas</option>
        </Select>
      </div>

      {error === null ? null : (
        <p
          role="alert"
          className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
        >
          {error}
        </p>
      )}

      {/* Announced, so a screen reader hears the count change rather than only
          seeing the table redraw. */}
      <p
        aria-live="polite"
        className="text-(length:--text-xs) text-(--text-muted)"
      >
        {query.isPending
          ? 'Carregando…'
          : methods.length === 0
            ? 'Nenhuma forma de pagamento'
            : `${String(methods.length)} forma(s) de pagamento`}
      </p>

      {query.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : query.isError ? (
        <EmptyState
          icon={<CreditCard aria-hidden="true" className="h-8 w-8" />}
          title="Não foi possível carregar as formas de pagamento"
          description="Verifique a conexão com o servidor e tente novamente."
        />
      ) : methods.length === 0 ? (
        <EmptyState
          icon={<Wallet aria-hidden="true" className="h-8 w-8" />}
          title={
            status === 'archived'
              ? 'Nenhuma forma arquivada'
              : 'Nenhuma forma de pagamento cadastrada'
          }
          description={
            status === 'archived'
              ? 'Formas arquivadas continuam legíveis nos pagamentos já registrados.'
              : canManage
                ? 'Comece pelas formas usuais — dinheiro, PIX, débito, crédito e boleto — ou cadastre uma por vez.'
                : 'Peça a um administrador para cadastrar as formas de pagamento.'
          }
          action={
            status === 'active' && canManage ? (
              <Button
                variant="primary"
                size="sm"
                loading={installDefaults.isPending}
                onClick={() => {
                  installDefaults.mutate()
                }}
              >
                Usar as formas usuais
              </Button>
            ) : undefined
          }
        />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Código</TableHeaderCell>
                <SortableHeaderCell {...sort.column('name')}>
                  Nome
                </SortableHeaderCell>
                <TableHeaderCell>Tipo</TableHeaderCell>
                <TableHeaderCell className="text-right">Taxa</TableHeaderCell>
                <TableHeaderCell>Recebimento</TableHeaderCell>
                <TableHeaderCell className="text-right">
                  Parcelas
                </TableHeaderCell>
                <TableHeaderCell>Contas a receber</TableHeaderCell>
                {/* Dropped entirely without the permission, rather than left
                    empty. A column headed "Ações" with nothing under it reads
                    as a broken screen, not as a permission boundary. */}
                {canManage ? (
                  <TableHeaderCell className="text-right">
                    Ações
                  </TableHeaderCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {methods.map((method, index) => (
                <TableRow key={method.id}>
                  <TableCell className="font-(--font-mono) whitespace-nowrap">
                    {method.code}
                  </TableCell>
                  <TableCell>
                    <span className="text-(--text-primary)">{method.name}</span>
                    {method.allowsChange ? (
                      <Badge tone="neutral" className="ml-2">
                        Dá troco
                      </Badge>
                    ) : null}
                    {method.status === 'archived' ? (
                      <Badge tone="neutral" className="ml-2">
                        Arquivada
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-(--text-secondary)">
                    {PAYMENT_METHOD_KIND_LABELS[method.kind]}
                  </TableCell>
                  <TableCell numeric>{feeLabel(method.feePercent)}</TableCell>
                  <TableCell className="text-(--text-secondary)">
                    {settlementLabel(method)}
                  </TableCell>
                  <TableCell numeric>
                    {method.maxInstalments === 1
                      ? '—'
                      : `${String(method.maxInstalments)}x`}
                  </TableCell>
                  {/* Stated per row rather than hidden in the edit dialog: it
                      decides whether a sale leaves a debt behind, and an admin
                      should be able to read the whole policy off one table. */}
                  <TableCell className="text-(--text-secondary)">
                    {method.generatesReceivable
                      ? method.receivableDueDays === 0
                        ? 'Gera, vence no dia'
                        : `Gera, ${String(method.receivableDueDays)} dias`
                      : 'Não gera'}
                  </TableCell>
                  {canManage ? (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {status === 'active' ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={index === 0 || reorder.isPending}
                              aria-label={`Subir ${method.name}`}
                              onClick={() => {
                                move(index, -1)
                              }}
                            >
                              <ArrowUp
                                aria-hidden="true"
                                className="h-3.5 w-3.5"
                              />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={
                                index === methods.length - 1 ||
                                reorder.isPending
                              }
                              aria-label={`Descer ${method.name}`}
                              onClick={() => {
                                move(index, 1)
                              }}
                            >
                              <ArrowDown
                                aria-hidden="true"
                                className="h-3.5 w-3.5"
                              />
                            </Button>
                          </>
                        ) : null}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            openEdit(method)
                          }}
                          leadingIcon={
                            <Pencil
                              aria-hidden="true"
                              className="h-3.5 w-3.5"
                            />
                          }
                        >
                          Editar
                        </Button>
                        {method.status === 'active' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setError(null)
                              archive.mutate(method)
                            }}
                            leadingIcon={
                              <Archive
                                aria-hidden="true"
                                className="h-3.5 w-3.5"
                              />
                            }
                          >
                            Arquivar
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Mounted only while open, and keyed by the method being edited, so the
          form starts from the right values without an effect that resets them.
          React's own answer to "reset state when a prop changes". */}
      {formOpen ? (
        <PaymentMethodForm
          key={editing?.id ?? 'new'}
          method={editing}
          onClose={() => {
            setFormOpen(false)
          }}
        />
      ) : null}
      {canManage ? <MoneddSettings /> : null}
    </>
  )
}
